// Вложения заявки: только PDF и изображения (решение владельца 29.10.2026). Те же лимиты проверяет
// сервер (server/app.mjs) — браузеру он не доверяет
export const ATTACHMENT_TYPES = {
  'application/pdf': '.pdf',
  'image/jpeg': '.jpg',
  'image/png': '.png',
} as const;

export type AttachmentType = keyof typeof ATTACHMENT_TYPES;

export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENTS_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

const BY_EXTENSION: Record<string, AttachmentType> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
};

const isAttachmentType = (type: string): type is AttachmentType =>
  Object.hasOwn(ATTACHMENT_TYPES, type);

/** Тип по MIME, а если браузер его не дал (бывает в Windows) — по расширению */
export const attachmentType = (file: { name: string; type: string }): AttachmentType | null => {
  if (isAttachmentType(file.type)) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return BY_EXTENSION[extension] ?? null;
};

interface IFileLike {
  name: string;
  type: string;
  size: number;
  lastModified: number;
}

const sameFile = (a: IFileLike, b: IFileLike) =>
  a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

/**
 * Добавляет выбранные файлы к уже прикреплённым. Неподходящие не берёт и объясняет почему —
 * одной фразой, по первой встретившейся причине
 */
export const addAttachments = <T extends IFileLike>(
  current: readonly T[],
  added: readonly T[],
): { files: T[]; error: string | null } => {
  const files = [...current];
  let error: string | null = null;
  let total = files.reduce((sum, f) => sum + f.size, 0);
  for (const file of added) {
    if (files.some((f) => sameFile(f, file))) continue;
    if (!attachmentType(file)) {
      error ??= `«${file.name}» — не PDF, JPG или PNG`;
      continue;
    }
    if (file.size === 0) {
      error ??= `«${file.name}» — пустой файл`;
      continue;
    }
    if (files.length >= MAX_ATTACHMENTS) {
      error ??= `Не больше ${MAX_ATTACHMENTS} файлов`;
      continue;
    }
    if (total + file.size > MAX_ATTACHMENTS_BYTES) {
      error ??= 'Всего не больше 10 МБ — пришлите крупные файлы письмом';
      continue;
    }
    files.push(file);
    total += file.size;
  }
  return { files, error };
};

/** «340 КБ», «1,2 МБ» */
export const formatSize = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} КБ`
    : `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;

/** Содержимое файла в base64 — так вложение едет в JSON заявки */
export const toBase64 = async (file: Blob): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  // Кусками: String.fromCharCode(...весь файл) переполняет стек на мегабайтах
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};
